import { FolderIcon } from "@heroicons/react/24/outline";
import { getGoalStartIndices, unwrapConnection } from "@utils/utils";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ReportDomainType, ReportGoalType } from "@api/graphql/types-and-hooks";
import { TemplateGoalReviewCard } from "@components/TreatmentPlanFieldForm/TemplateGoalReviewCard";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { useGoals } from "@hooks/useGoals";
import { useReport } from "@providers/ReportProvider";

interface TreatmentPlanFieldProps {
  fieldTitle?: string | null;
  showMedicalNecessity?: boolean;
}

export const TreatmentPlanField: React.FC<TreatmentPlanFieldProps> = ({ fieldTitle, showMedicalNecessity = false }) => {
  const { assessmentId: reportId } = useParams();
  const { report } = useReport();
  const isGasEnabled = report.reportTemplate?.treatmentPlanTemplate?.isGasEnabled || false;

  const { goalsQuery } = useGoals({ reportId });
  const { goalFields, targetFields } = useTreatmentPlanTemplate(report.id);

  const domains = useMemo(() => {
    return unwrapConnection(goalsQuery.data?.getReports?.edges?.[0]?.node?.reportTreatmentPlan?.reportdomainSet);
  }, [goalsQuery.data]);

  if (!domains?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Paragraph displayType="normal" colorType="secondary">
          No treatment plan goals have been added yet.
        </Paragraph>
      </div>
    );
  }

  const handleGoalMutationSuccess = () => {
    goalsQuery.refetch();
  };

  // Goals are numbered continuously across domains (1..N, not restarting per
  // domain). Precompute each domain's in-use goals plus its starting offset
  // (the count of in-use goals in all domains before it).
  const goalsByDomain = domains.map((domain) =>
    (unwrapConnection(domain?.reportgoalSet) || []).filter((g) => g?.inUse),
  );
  const domainGoalOffsets = getGoalStartIndices(goalsByDomain.map((goals) => goals.length));

  return (
    <div className="flex flex-col gap-y-6 pt-4">
      {fieldTitle && (
        <Label size="small" colorType="secondary" className="pt-2">
          {fieldTitle}
        </Label>
      )}
      {domains.map((domain, domainIndex) => {
        const domainId = `heading-${domain?.name?.toLowerCase().split(" ").join("-")}`;
        const goals = goalsByDomain[domainIndex];
        if (goals.length === 0) return null;
        const goalNumberOffset = domainGoalOffsets[domainIndex];
        return (
          <div key={domain?.name} className="pageBreak">
            <div id={domainId} className="flex items-center gap-x-2 mb-4 scroll-mt-46" data-sticky-nav-header>
              <FolderIcon className="h-5 w-5 text-text-secondary" />
              <Paragraph displayType="loud" colorType="primary">
                {domain?.name}
              </Paragraph>
            </div>
            <div className="flex w-full flex-col space-y-2 items-center border border-limestone-100 rounded-2xl bg-limestone-50 p-2">
              {goals.map((goal, goalIndex) => (
                <TemplateGoalReviewCard
                  key={goal.id}
                  goal={goal as ReportGoalType}
                  domain={domain as ReportDomainType}
                  report={report}
                  goalFields={goalFields}
                  targetFields={targetFields}
                  isGasEnabled={isGasEnabled}
                  showMedicalNecessity={showMedicalNecessity}
                  onGoalMutationSuccess={handleGoalMutationSuccess}
                  goalIndex={goalNumberOffset + goalIndex}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
